import type { Party } from '@my/directory-api';
import {
  useDriverRosterConfigs,
  useEquipmentCatalogue,
  useOrganisation,
  useOrganisationEquipmentTypes,
  useParties,
  useResources,
  useSitesWithMembers,
  useSubcontractorTrailers,
} from '@my/directory-api/hooks';
import { useMemo } from 'react';
import {
  ALWAYS_VISIBLE_POOLS,
  byTitle,
  channelFor,
  CHANNEL_ACCENT,
  CHANNEL_LABEL,
  driverSubtitle,
  equipmentLabels,
  forDepot,
  POOL_ORDER,
  resourceCard,
  type LinkedVehicle,
  type PoolId,
  type ResourceCardVM,
} from '../lib/planResources';
import { subcontractorTrailerCard } from '../lib/subcontractorTrailers';
import { parsePlanFeatures, type PlanFeatures } from '../lib/planFeatures';

/** `<select>` row — id plus the label to show. */
export interface PickOption {
  id: string;
  label: string;
}

export interface PlanResources {
  /** Depot-filtered cards per pool. */
  byPool: Record<PoolId, ResourceCardVM[]>;
  /**
   * Pools with at least one card, in display order, plus any in
   * {@link ALWAYS_VISIBLE_POOLS}.
   */
  visiblePools: PoolId[];
  /**
   * What this organisation is offered in the cockpit. Returned as well as
   * `visiblePools` because the Gantt is gated by the same document but is not
   * a pool — the page reads `planFeatures.gantt` rather than deriving it.
   */
  planFeatures: PlanFeatures;
  /** Haulier suppliers, for the add-subcontractor-trailer form. */
  subcontractorOptions: PickOption[];
  /** Org trailer equipment types, for the add-subcontractor-trailer form. */
  equipmentTypeOptions: PickOption[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Assembles the cockpit's resource pools — rostered drivers, drivers not yet on
 * the roster, trailers, vehicles (trucks + rigids) and subcontractors — filtered
 * to the selected depot.
 *
 * Lives here rather than inside a component because both the resource strip and
 * the Gantt render the same pool: one shared hook keeps them in lockstep and
 * avoids running the queries twice.
 */
export function usePlanResources(
  organisationId: string | null,
  depotSiteId: string | null,
): PlanResources {
  const configsQuery = useDriverRosterConfigs(organisationId);
  const resourcesQuery = useResources(organisationId);
  const suppliersQuery = useParties(organisationId, 'Supplier');
  const trailersQuery = useSubcontractorTrailers(organisationId);
  const equipmentTypesQuery = useOrganisationEquipmentTypes(organisationId);
  const catalogueQuery = useEquipmentCatalogue();
  const sitesQuery = useSitesWithMembers(organisationId);
  // The org record carries which pools this organisation is offered, and
  // whether it gets a Gantt. Shared cache with every other TMS screen, so this
  // is almost always warm by the time the cockpit mounts.
  const organisationQuery = useOrganisation(organisationId);

  /** Site id → name, for the location line's depot fallback. */
  const siteNameById = useMemo(
    () => new Map((sitesQuery.data ?? []).map((site) => [site.id, site.name])),
    [sitesQuery.data],
  );

  const labelByKey = useMemo(() => equipmentLabels(catalogueQuery.data), [catalogueQuery.data]);
  /** Org equipment-type row id → display label (for trailer / rigid cards). */
  const equipmentNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of equipmentTypesQuery.data ?? []) {
      m.set(r.id, labelByKey.get(r.catalogueKey) ?? r.catalogueKey);
    }
    return m;
  }, [equipmentTypesQuery.data, labelByKey]);

  // Memoised so the `?? []` fallback doesn't hand the card memos a fresh array
  // (and a fresh dependency) on every render.
  const resources = useMemo(() => resourcesQuery.data ?? [], [resourcesQuery.data]);
  const configs = useMemo(() => configsQuery.data ?? [], [configsQuery.data]);

  /**
   * Driver ⇄ default-vehicle, both directions, resolved from the single resource
   * list the page already loads — no extra round trips. `vehicleById` feeds the
   * driver card's reg; `driverByVehicleId` is what makes a vehicle read "In use"
   * in the picker.
   */
  const vehicleById = useMemo(() => new Map(resources.map((r) => [r.id, r])), [resources]);
  const driversByVehicleId = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const r of resources) {
      if (r.type !== 'Driver' || !r.defaultVehicleResourceId) continue;
      // A vehicle can legitimately be shared (double-shifting), so collect every
      // driver rather than picking one — the planner needs to see who they'd be
      // taking it from.
      const names = m.get(r.defaultVehicleResourceId) ?? [];
      names.push(r.displayName || r.name);
      m.set(r.defaultVehicleResourceId, names);
    }
    for (const names of m.values()) names.sort((a, b) => a.localeCompare(b));
    return m;
  }, [resources]);

  /** A driver's default vehicle as the card's linked-vehicle block. */
  const linkedVehicleFor = useMemo(
    () =>
      (driver: { defaultVehicleResourceId: string | null }): LinkedVehicle | null => {
        if (!driver.defaultVehicleResourceId) return null;
        const v = vehicleById.get(driver.defaultVehicleResourceId);
        if (!v) return null;
        return {
          resourceId: v.id,
          reg: v.reference || v.displayName || v.name,
          // Telematics isn't wired — knowing the vehicle doesn't give us its state.
          tachoStatus: 'Unknown',
        };
      },
    [vehicleById],
  );

  // Rostered drivers — config carries the planned start + location.
  const rosterCards = useMemo<ResourceCardVM[]>(() => {
    const byId = new Map(resources.map((r) => [r.id, r]));
    return configs
      .map((c) => {
        const r = byId.get(c.resourceId);
        if (!r) return null;
        return resourceCard({
          resource: r,
          equipmentNameById,
          siteNameById,
          subtitle: driverSubtitle(c.defaultStartTime, c.startLocation),
          showTacho: true,
          linkedVehicle: linkedVehicleFor(r),
        });
      })
      .filter((c): c is ResourceCardVM => c !== null)
      .sort(byTitle);
  }, [configs, resources, equipmentNameById, siteNameById, linkedVehicleFor]);

  // The org record carries which pools this organisation is offered, and
  // whether it gets a Gantt. Read before the driver pool because that pool's
  // contents depend on whether the Driver Roster pool exists at all.
  const planFeatures = useMemo(
    () => parsePlanFeatures(organisationQuery.data?.tmsPlanFeaturesJson),
    [organisationQuery.data?.tmsPlanFeaturesJson],
  );
  const rosterOffered = planFeatures.pools.driverRoster;

  // Drivers with no roster config — the pool you'd add to the roster. No config
  // means no start time or location, hence the bare "Unknown location".
  //
  // When the organisation is NOT offered the Driver Roster pool, this is the
  // only place a driver can appear, so it lists every driver — rostered ones
  // included, with their roster start/location — and drops the "Not on the
  // roster" note, which means nothing to an org that has no roster tab.
  const driverCards = useMemo<ResourceCardVM[]>(() => {
    const configById = new Map(configs.map((c) => [c.resourceId, c]));
    return resources
      .filter((r) => r.type === 'Driver' && (!rosterOffered || !configById.has(r.id)))
      .map((r) => {
        const config = configById.get(r.id);
        return resourceCard({
          resource: r,
          equipmentNameById,
          siteNameById,
          subtitle: driverSubtitle(config?.defaultStartTime ?? null, config?.startLocation ?? null),
          note: rosterOffered ? 'Not on the roster' : null,
          showTacho: true,
          linkedVehicle: linkedVehicleFor(r),
        });
      })
      .sort(byTitle);
  }, [configs, resources, rosterOffered, equipmentNameById, siteNameById, linkedVehicleFor]);

  const trailerCards = useMemo<ResourceCardVM[]>(
    () =>
      resources
        .filter((r) => r.type === 'Trailer')
        .map((r) =>
          resourceCard({
            resource: r,
            equipmentNameById,
            siteNameById,
            subtitle: r.reference || '—',
          }),
        )
        .sort(byTitle),
    [resources, equipmentNameById, siteNameById],
  );

  const vehicleCards = useMemo<ResourceCardVM[]>(
    () =>
      resources
        .filter((r) => r.type === 'Truck' || r.type === 'Rigid')
        .map((r) =>
          resourceCard({
            resource: r,
            equipmentNameById,
            siteNameById,
            subtitle: r.reference || '—',
            linkedDriverNames: driversByVehicleId.get(r.id) ?? [],
          }),
        )
        .sort(byTitle),
    [resources, equipmentNameById, siteNameById, driversByVehicleId],
  );

  // Subcontractors — Haulier suppliers (a load's subcontractor is a party).
  const subcontractorCards = useMemo<ResourceCardVM[]>(
    () =>
      (suppliersQuery.data ?? [])
        .filter((p: Party) => p.subtype === 'Haulier')
        .map((p) => ({
          id: p.id,
          title: p.name,
          subtitle: p.code || '—',
          meta: p.primaryContactName,
          resourceType: null,
          typeChip: null,
          caps: (p.tags ?? '')
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
            .map((t) => ({ label: t, serviceTag: true })),
          available: p.status === 'Active',
          showTacho: false,
          linkedVehicle: null,
          note: null,
          // Subcontractors are parties, not resources — no depot, so the depot
          // selector never filters them out, and no certs to raise alerts from.
          homeSiteId: null,
          depotLabel: null,
          currentLocationLabel: null,
          odometerKm: null,
          alerts: [],
          linkedDriverNames: [],
          // A network id means they're on the exchange; otherwise it's email.
          channel: channelFor(p.networkId),
          // Primary-address summary rides along on the party DTO, so the card
          // gets a location without a fetch per row. City is what's shown; the
          // full line is the tooltip.
          cityLabel: p.primaryAddressCity?.trim() || null,
          addressLabel: p.primaryFullAddress?.trim() || null,
          countryCode: p.primaryAddressCountryIsoCode?.trim() || null,
          // Zero until assignment exists — that's a later piece of work. The
          // meter renders the shape now so the cap has something to sit in.
          assignedCount: 0,
          acceptedCount: 0,
          assignmentCap: p.maxConcurrentAssignments,
          // Top-right corner carries the channel for a subcontractor.
          topBadge: {
            label: CHANNEL_LABEL[channelFor(p.networkId)],
            accent: CHANNEL_ACCENT[channelFor(p.networkId)],
          },
          // A subcontractor card IS the party, so it supplies itself.
          subcontractorPartyId: p.id,
          isSubcontractorUnit: false,
        }))
        .sort(byTitle),
    [suppliersQuery.data],
  );

  /** Haulier party id → name + channel, for resolving a trailer's supplier. */
  const hauliersById = useMemo(() => {
    const m = new Map<string, Party>();
    for (const p of suppliersQuery.data ?? []) {
      if (p.subtype === 'Haulier') m.set(p.id, p);
    }
    return m;
  }, [suppliersQuery.data]);

  // Subcontractor trailers — units a haulier has left with us.
  const subcontractorTrailerCards = useMemo<ResourceCardVM[]>(
    () =>
      (trailersQuery.data ?? [])
        .map((trailer) => {
          const haulier = hauliersById.get(trailer.subcontractorPartyId) ?? null;
          return subcontractorTrailerCard({
            trailer,
            // The DTO carries the catalogue key rather than a label; resolve it
            // through the same catalogue the fleet cards use.
            equipmentName: trailer.equipmentCatalogueKey
              ? (labelByKey.get(trailer.equipmentCatalogueKey) ?? trailer.equipmentCatalogueKey)
              : null,
            channel: haulier ? channelFor(haulier.networkId) : null,
          });
        })
        .sort(byTitle),
    [trailersQuery.data, hauliersById, labelByKey],
  );

  const subcontractorOptions = useMemo<PickOption[]>(
    () =>
      [...hauliersById.values()]
        .map((p) => ({ id: p.id, label: p.name }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
    [hauliersById],
  );

  const equipmentTypeOptions = useMemo<PickOption[]>(
    () =>
      (equipmentTypesQuery.data ?? [])
        .map((r) => ({ id: r.id, label: equipmentNameById.get(r.id) ?? r.catalogueKey }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
    [equipmentTypesQuery.data, equipmentNameById],
  );

  const byPool = useMemo<Record<PoolId, ResourceCardVM[]>>(
    () => ({
      driverRoster: forDepot(rosterCards, depotSiteId),
      drivers: forDepot(driverCards, depotSiteId),
      trailers: forDepot(trailerCards, depotSiteId),
      vehicles: forDepot(vehicleCards, depotSiteId),
      subcontractors: subcontractorCards,
      // Not depot-filtered: the unit sits in the haulier's yard, not one of ours.
      subcontractorTrailers: subcontractorTrailerCards,
    }),
    [
      rosterCards,
      driverCards,
      trailerCards,
      vehicleCards,
      subcontractorCards,
      subcontractorTrailerCards,
      depotSiteId,
    ],
  );

  // Two independent reasons a pool is absent, and they compose:
  //   1. The ORGANISATION is not offered it — support's call, stored on the
  //      Directory org record. A missing entry means offered.
  //   2. It is EMPTY today — the pre-existing rule, so an org with every driver
  //      rostered never sees a "Drivers" tab.
  // Order matters: an org that is not offered Subcontractor Trailers must not
  // get the tab back through ALWAYS_VISIBLE_POOLS, whose whole purpose is to
  // keep the Add card reachable for orgs that DO have the feature.

  // Nothing until the org answer is in. The strip's `isLoading` covers the card
  // area but NOT the tab row, so without this a pool the org is not offered
  // would appear the moment resources land and vanish when the org record
  // follows — which reads as a bug and invites a click on the way out. The org
  // query is shared with every other TMS screen and cached for five minutes, so
  // this normally costs nothing.
  const organisationPending = organisationQuery.isPending;

  const visiblePools = useMemo(
    () =>
      organisationPending
        ? []
        : POOL_ORDER.filter(
            (id) =>
              planFeatures.pools[id] && (byPool[id].length > 0 || ALWAYS_VISIBLE_POOLS.has(id)),
          ),
    [byPool, planFeatures, organisationPending],
  );

  return {
    byPool,
    visiblePools,
    planFeatures,
    subcontractorOptions,
    equipmentTypeOptions,
    isLoading:
      resourcesQuery.isLoading ||
      configsQuery.isLoading ||
      suppliersQuery.isLoading ||
      trailersQuery.isLoading ||
      organisationQuery.isLoading,
    isError: resourcesQuery.isError || configsQuery.isError || trailersQuery.isError,
  };
}
